import { useCallback, useEffect, useMemo, useState } from "react";
import {
  currentFeedingTarget,
  lateFeedingTargets,
  loadFeedingSnapshot,
  readFeedingSnapshotCache,
  writeFeedingSnapshotCache,
  type FeedingSnapshot,
  type FeedingTarget,
} from "../feeding/liveFeeding";
import {
  buildFeedingDayPrepareOperation,
  buildFeedingOperation,
  type AppetiteLevel,
  type LeftoverLevel,
} from "../operations/workerOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OutboxSummary } from "../sync/types";
import { supabase } from "../auth/supabaseClient";
import { ConfirmationSheet } from "./ConfirmationSheet";

interface FeedingPanelProps {
  farmId: string;
  userId: string;
  preferredTankId?: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

interface PendingFeeding {
  target: FeedingTarget;
  productId: string;
  productName: string;
  unit: string;
  amount: number;
  appetite: AppetiteLevel;
  leftover: LeftoverLevel;
  notes?: string;
}

function roundTime(snapshot: FeedingSnapshot, target: FeedingTarget): string {
  return new Date(target.run.scheduledAt).toLocaleTimeString("fa-IR", {
    timeZone: snapshot.timezoneName,
    hour: "2-digit",
    minute: "2-digit",
  });
}

function appetiteLabel(value: AppetiteLevel): string {
  if (value === "low") return "🔴 کم";
  if (value === "high") return "🟢 زیاد";
  return "🟡 متوسط";
}

function leftoverLabel(value: LeftoverLevel): string {
  if (value === "none") return "✅ بدون باقی‌مانده";
  if (value === "low") return "🟡 کم";
  return "🔴 زیاد";
}

function syncMessage(status: string): string {
  if (status === "synced") return "غذا ثبت و همگام شد.";
  if (status === "pending") return "غذا آفلاین ثبت شد و در صف ارسال قرار گرفت.";
  if (status === "retryable_error") return "ثبت محلی حفظ شد؛ ارسال دوباره انجام می‌شود.";
  if (status === "terminal_conflict") return "ثبت محلی حفظ شد اما نیاز به بررسی تعارض دارد.";
  return "ثبت غذا انجام شد.";
}

export function FeedingPanel({
  farmId,
  userId,
  preferredTankId,
  onSyncSummary,
}: FeedingPanelProps) {
  const [snapshot, setSnapshot] = useState<FeedingSnapshot>();
  const [source, setSource] = useState<"live" | "cache" | "empty">("empty");
  const [mode, setMode] = useState<"current" | "late">("current");
  const [selectedLateId, setSelectedLateId] = useState<string>();
  const [productId, setProductId] = useState("");
  const [amount, setAmount] = useState("");
  const [appetite, setAppetite] = useState<AppetiteLevel>("normal");
  const [leftover, setLeftover] = useState<LeftoverLevel>("none");
  const [notes, setNotes] = useState("");
  const [pending, setPending] = useState<PendingFeeding>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const refresh = useCallback(async () => {
    setError(undefined);

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      const cached = readFeedingSnapshotCache(farmId);
      if (cached) {
        setSnapshot(cached);
        setSource("cache");
      } else {
        setSnapshot(undefined);
        setSource("empty");
      }
      return;
    }

    try {
      let live = await loadFeedingSnapshot(supabase, { farmId });

      if (live.runs.length === 0) {
        const prepare = buildFeedingDayPrepareOperation({
          actorId: userId,
          farmId,
          deviceId: getOrCreateDeviceId(),
          localDate: live.localDate,
          performedAt: new Date().toISOString(),
        });
        const prepared = await persistAndSyncOperation(supabase, prepare, true);
        onSyncSummary?.(prepared.summary);

        if (prepared.operation.status === "terminal_conflict") {
          throw new Error(prepared.operation.lastErrorCode ?? "FEEDING_DAY_PREPARE_FAILED");
        }

        live = await loadFeedingSnapshot(supabase, { farmId });
      }

      writeFeedingSnapshotCache(live);
      setSnapshot(live);
      setSource("live");
    } catch (caught) {
      const cached = readFeedingSnapshotCache(farmId);
      if (cached) {
        setSnapshot(cached);
        setSource("cache");
        setError("به‌روزرسانی آنلاین انجام نشد؛ صف ذخیره‌شده محلی نمایش داده می‌شود.");
      } else {
        setError(caught instanceof Error ? caught.message : "بارگذاری برنامه غذا انجام نشد.");
        setSource("empty");
      }
    }
  }, [farmId, onSyncSummary, userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleScheduleChanged = () => {
      void refresh();
    };

    window.addEventListener("discus:v2:feeding-schedule-changed", handleScheduleChanged);
    return () => {
      window.removeEventListener("discus:v2:feeding-schedule-changed", handleScheduleChanged);
    };
  }, [refresh]);

  const current = useMemo(
    () => (snapshot ? currentFeedingTarget(snapshot, new Date()) : undefined),
    [snapshot],
  );
  const late = useMemo(
    () => (snapshot ? lateFeedingTargets(snapshot, new Date()) : []),
    [snapshot],
  );

  useEffect(() => {
    if (preferredTankId) {
      if (current?.item.tankId === preferredTankId) {
        setMode("current");
        return;
      }

      const preferredLate = late.find((candidate) => candidate.item.tankId === preferredTankId);
      if (preferredLate) {
        setMode("late");
        setSelectedLateId(preferredLate.item.id);
        return;
      }
    }

    if (!current && late.length > 0) {
      setMode("late");
    } else if (current) {
      setMode("current");
    }
  }, [current, late, preferredTankId]);

  useEffect(() => {
    if (
      snapshot?.products.length &&
      !snapshot.products.some((product) => product.id === productId)
    ) {
      setProductId(snapshot.products[0]?.id ?? "");
    }
  }, [productId, snapshot]);

  const selectedLate = late.find((target) => target.item.id === selectedLateId) ?? late[0];
  const target = mode === "late" ? selectedLate : current;

  function requestConfirmation() {
    setMessage(undefined);
    setError(undefined);

    if (!snapshot || !target) {
      setError("تانک قابل ثبت برای این نوبت پیدا نشد.");
      return;
    }

    const product = snapshot.products.find((candidate) => candidate.id === productId);
    const numericAmount = Number(amount);

    if (!product) {
      setError("نوع غذا را انتخاب کنید.");
      return;
    }

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError("مقدار غذا باید بیشتر از صفر باشد.");
      return;
    }

    setPending({
      target,
      productId: product.id,
      productName: product.displayName,
      unit: product.defaultUnit,
      amount: numericAmount,
      appetite,
      leftover,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
  }

  async function confirmFeeding() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const queued = buildFeedingOperation(
        {
          actorId: userId,
          farmId,
          deviceId: getOrCreateDeviceId(),
          tankId: pending.target.item.tankId,
          performedAt: new Date().toISOString(),
          ...(pending.notes ? { notes: pending.notes } : {}),
        },
        {
          roundTankId: pending.target.item.id,
          feedProductId: pending.productId,
          amount: pending.amount,
          unit: pending.unit,
          appetiteLevel: pending.appetite,
          leftoverLevel: pending.leftover,
        },
      );

      const result = await persistAndSyncOperation(
        supabase,
        queued,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );

      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);
      setAmount("");
      setNotes("");

      if (result.operation.status === "synced") {
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت غذا انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  if (!snapshot) {
    return (
      <section className="section-card">
        <p className="section-kicker">تغذیه</p>
        <h2>برنامه غذای سالن</h2>
        <p className="section-note">
          {source === "empty" ? "اطلاعات برنامه غذا در دسترس نیست." : "در حال بارگذاری…"}
        </p>
        {error ? <p className="settings-warning">{error}</p> : null}
        <button
          type="button"
          className="secondary-button large-button"
          onClick={() => void refresh()}
        >
          تلاش مجدد
        </button>
      </section>
    );
  }

  return (
    <section className="section-card" aria-labelledby="feeding-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">تغذیه</p>
          <h2 id="feeding-title">برنامه غذای سالن</h2>
        </div>
        <span className="muted-chip">{source === "live" ? "آنلاین" : "آفلاین"}</span>
      </div>

      {current ? (
        <button
          type="button"
          className={mode === "current" ? "feeding-target active" : "feeding-target"}
          onClick={() => setMode("current")}
        >
          <span>
            <strong>تانک {current.item.tankCode}</strong>
            <small>
              دور {current.run.roundNo} · ساعت {roundTime(snapshot, current)} · صف{" "}
              {current.item.queueOrder}/{current.run.totalTanks}
            </small>
          </span>
          <span className="work-status">نوبت جاری</span>
        </button>
      ) : (
        <p className="empty-state">در این لحظه تانک در انتظار غذادهیِ نوبت جاری وجود ندارد.</p>
      )}

      {late.length > 0 ? (
        <div className="feeding-late-block">
          <div className="section-title-row">
            <strong>غذادهی‌های با تأخیر</strong>
            <span className="count-pill">{late.length}</span>
          </div>
          <div className="feeding-late-list">
            {late.map((lateTarget) => (
              <button
                type="button"
                className={
                  mode === "late" && selectedLate?.item.id === lateTarget.item.id
                    ? "feeding-late-button active"
                    : "feeding-late-button"
                }
                key={lateTarget.item.id}
                onClick={() => {
                  setMode("late");
                  setSelectedLateId(lateTarget.item.id);
                }}
              >
                <strong>{lateTarget.item.tankCode}</strong>
                <span>دور {lateTarget.run.roundNo}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {target ? (
        <div className="feeding-form">
          <div>
            <p className="section-kicker">نوع غذا</p>
            <div className="quick-grid">
              {snapshot.products.map((product) => (
                <button
                  type="button"
                  className={productId === product.id ? "quick-button active" : "quick-button"}
                  key={product.id}
                  onClick={() => setProductId(product.id)}
                >
                  <span aria-hidden="true">🍽️</span>
                  <strong>{product.displayName}</strong>
                </button>
              ))}
            </div>
          </div>

          <label className="operation-field">
            <span>
              مقدار غذا ({snapshot.products.find((p) => p.id === productId)?.defaultUnit ?? "g"})
            </span>
            <input
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              type="number"
              inputMode="decimal"
              step="any"
              min="0"
            />
          </label>

          <div>
            <p className="section-kicker">اشتها</p>
            <div className="three-choice-grid">
              {(
                [
                  ["low", "🔴 کم"],
                  ["normal", "🟡 متوسط"],
                  ["high", "🟢 زیاد"],
                ] as const
              ).map(([value, label]) => (
                <button
                  type="button"
                  className={appetite === value ? "choice-button active" : "choice-button"}
                  key={value}
                  onClick={() => setAppetite(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="section-kicker">غذای باقی‌مانده</p>
            <div className="three-choice-grid">
              {(
                [
                  ["none", "✅ بدون باقی‌مانده"],
                  ["low", "🟡 کم"],
                  ["high", "🔴 زیاد"],
                ] as const
              ).map(([value, label]) => (
                <button
                  type="button"
                  className={leftover === value ? "choice-button active" : "choice-button"}
                  key={value}
                  onClick={() => setLeftover(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <label className="operation-field">
            <span>یادداشت</span>
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} />
          </label>

          {message ? <p className="operation-success">{message}</p> : null}
          {error ? <p className="settings-warning">{error}</p> : null}

          <button
            type="button"
            className="patrol-button"
            disabled={busy}
            onClick={requestConfirmation}
          >
            <span aria-hidden="true">✓</span>
            <span>
              <strong>{mode === "late" ? "ثبت غذادهی با تأخیر" : "ادامه ثبت غذا"}</strong>
              <small>قبل از ثبت نهایی، خلاصه نمایش داده می‌شود</small>
            </span>
          </button>
        </div>
      ) : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید غذادهی"
          parts={[
            `تانک ${pending.target.item.tankCode}`,
            `دور ${pending.target.run.roundNo}`,
            pending.target.mode === "late" ? "ثبت با تأخیر" : "نوبت جاری",
            `${pending.productName}، ${pending.amount} ${pending.unit}`,
            `اشتها: ${appetiteLabel(pending.appetite)}`,
            `باقی‌مانده: ${leftoverLabel(pending.leftover)}`,
          ]}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirmFeeding()}
        />
      ) : null}
    </section>
  );
}
